import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { CashRegister, CashRegisterStatus } from '@api/CashRegister/CashRegister';
import { CashFlow } from '@api/CashFlows/CashFlow';
import { Order } from '@api/Orders/Order';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';
import { FirestoreDateLike, toMillisSafe } from '@utils/Date';
import { isCashOtherInput, isCashOutput, isCashShipping } from '@utils/CashFlows';

/**
 * Reparto del dinero de un día entre las cajas de una sucursal.
 *
 * ⚠️ El problema de fondo: ni `cash-flows` ni `orders` guardan a qué caja
 * pertenecen (`CashFlow` solo tiene `branchOfficeId`, `userId` y `date`). Como el
 * negocio abre, cierra y vuelve a abrir caja en el mismo día (turnos), atribuir
 * por sucursal+día ya no alcanza: un corte por turno no significaría nada.
 *
 * La solución sin tocar el schema de órdenes ni de flujos: **cada caja tiene una
 * VENTANA de tiempo** — de su apertura a su cierre (`closedAt`) — y un movimiento
 * pertenece a la caja cuya ventana lo contiene. Lo que cae fuera de toda ventana
 * (se vendió sin caja abierta, o entre el cierre de una y la apertura de la
 * siguiente) se reporta aparte en `outside`: es justo el hueco que hay que ver.
 *
 * La fórmula del efectivo esperado es la misma del informe de ventas
 * (`getFinalCashRegister`), incluida la sutileza de excluir los `Input` que traen
 * `orderId`: esos son el ingreso en efectivo que generó la orden al completarse y
 * ya vienen contados en las ventas en efectivo. Contarlos otra vez duplicaría.
 */

const sum = <T>(items: T[], value: (item: T) => number): number =>
    items.reduce((total, item) => total + (value(item) || 0), 0);

/**
 * La fecha de una caja en milisegundos. Aquí se ordenan y cortan ventanas por
 * hora, así que el contrato importa: **0 cuando no hay fecha utilizable, nunca
 * `NaN`** — ver `toMillisSafe`.
 *
 * Tuvo su propia implementación mientras `parseFirebaseDate` estuvo roto; desde
 * el 2026-09-11 delega en `@utils/Date`, que es la única copia.
 */
export const getCashRegisterMillis = (date: FirestoreDateLike): number => toMillisSafe(date);

export const getCashRegisterDate = (date: FirestoreDateLike): Date => new Date(getCashRegisterMillis(date));

/** ¿Las dos fechas caen en el mismo día natural? */
const isSameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * ¿La caja que traemos en Redux sirve para operar AQUÍ y AHORA?
 *
 * Regla única para la barra superior y para el POS: una caja cerrada, de OTRA
 * sucursal, o **de otro día**, no habilita a vender.
 *
 * ⚠️ **Lo del día se agregó el 2026-08-29 y era el agujero grande.** Esta función
 * solo miraba estado y sucursal, así que una caja abierta hace días seguía
 * habilitando el POS indefinidamente. `getTodayOpenCashRegister` ya decía que una
 * caja de ayer NO se adopta *"(mezclaría dos días de efectivo)"* — pero solo se
 * llegaba a esa adopción si la caja que traía la sesión ya no servía, y con esta
 * función siempre servía. Medido en producción ese día: **32 cajas abiertas de
 * días anteriores** y **1,573 órdenes por $466,027** dentro de ventanas de varios
 * días, con cajas de hasta 6 días. Y se retroalimentaba: como el POS aceptaba la
 * caja vieja, al cajero nunca le aparecía el modal de abrir caja, así que nunca
 * cerraba la del día anterior.
 *
 * El corte es a MEDIANOCHE, igual que `getTodayOpenCashRegister`, porque el corte
 * de caja y el informe de ventas son por día natural. Consecuencia deliberada: un
 * turno que cruza la medianoche pide abrir caja nueva — que es lo correcto, esas
 * ventas ya son del día siguiente.
 *
 * Se toleran las cajas sin `branchOfficeId` (existieron antes de que se guardara
 * la sucursal) y las que no traen fecha utilizable: falta de dato no es motivo
 * para dejar a alguien sin vender.
 */
export const isActiveCashRegister = (
    cashRegister: CashRegister | undefined,
    branchOfficeId: string | undefined,
    now: Date = new Date()
): boolean => {
    if (!cashRegister) return false;
    if (cashRegister.status === CashRegisterStatus.Closed) return false;
    const openedMillis = getCashRegisterMillis(cashRegister.date);
    if (openedMillis && !isSameDay(new Date(openedMillis), now)) return false;
    if (!cashRegister.branchOfficeId) return true;
    return cashRegister.branchOfficeId === branchOfficeId;
};

/**
 * Las cajas que BLOQUEAN abrir una nueva en esa sucursal: abiertas, de un día
 * anterior y de esa misma sucursal.
 *
 * ## Por qué existe (decisión de Irving, 2026-09-10)
 * Se cerraron a mano **130 cajas en agosto, 32 el 29-ago y 95 el 10-sep**: es la
 * tercera vez y va en aumento, y **no hay causa de código** — `isActiveCashRegister`
 * ya obliga a abrir caja nueva cada día y el aviso interno "caja sin cerrar" ya
 * existe. Está concentrado en tres sucursales (65 de las 95), o sea es hábito de
 * turno. Así que el candado se mueve al único momento en que la persona
 * indicada está enfrente: **al abrir la caja del día siguiente.**
 *
 * ⚠️ Esto se había descartado antes, y la objeción sigue siendo cierta: **quien
 * llega no puede contar el efectivo de un turno ajeno.** Irving la resolvió por
 * la vía del negocio: que cierren la anterior aunque sea en cero, y que la
 * decisión de con cuánto cerrarla sea suya. Por eso el cierre tardío **no
 * finge un corte ciego**: no calcula esperado ni diferencia (ver
 * `CashRegisterServ.closeStaleCashRegister`).
 *
 * Una caja sin fecha utilizable NO bloquea: no se puede afirmar que sea vieja, y
 * dejar a alguien sin vender por un dato roto es peor que una caja de más.
 */
export const getBlockingCashRegisters = (
    registers: CashRegister[],
    branchOfficeId?: string,
    now: Date = new Date()
): CashRegister[] => {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    return (registers || []).filter((register) => {
        if (register?.status !== CashRegisterStatus.Open) return false;
        if (branchOfficeId && register.branchOfficeId !== branchOfficeId) return false;
        const millis = getCashRegisterMillis(register.date);
        if (!millis) return false;
        return millis < startOfToday.getTime();
    });
};

/**
 * Fin del día de una caja: es el `closedAt` que le corresponde a un cierre
 * TARDÍO. ⚠️ **Nunca la fecha de hoy**: la ventana de una caja va de su apertura
 * a su cierre, así que cerrarla "ahora" la estiraría sobre los días intermedios
 * y se tragaría el dinero de las cajas de en medio. Mismo criterio que
 * `scripts/cerrar-cajas-abiertas.mjs`.
 */
export const getEndOfCashRegisterDay = (register: CashRegister): Date => {
    const end = getCashRegisterDate(register.date);
    end.setHours(23, 59, 59, 999);
    return end;
};

export interface CashMovements {
    cashSales: number;
    cardSales: number;
    transferSales: number;
    totalSales: number;
    ordersCount: number;
    otherInputs: number;
    otherOutputs: number;
    shipping: number;
}

export interface CashRegisterSummary extends CashMovements {
    register: CashRegister;
    isOpen: boolean;
    /** Ventana [from, to) que le toca a esta caja */
    from: Date;
    to: Date;
    initialCash: number;
    /** fondo + ventas en efectivo + otros ingresos − otros gastos − envíos */
    expectedCash: number;
    /** Conteo ciego, si la caja se cerró con corte */
    countedCash?: number;
    difference?: number;
    hasBlindCount: boolean;
}

export interface BranchCashSummary extends CashMovements {
    branchOfficeId: string;
    branchOfficeName: string;
    registers: CashRegisterSummary[];
    openRegisters: number;
    hasRegister: boolean;
    initialCash: number;
    expectedCash: number;
    /** Dinero del día que NO cayó dentro de ninguna caja (se vendió sin caja abierta) */
    outside: CashMovements & { expectedCash: number };
    /** Suma de diferencias de las cajas que sí tienen corte ciego */
    difference: number;
    registersWithCount: number;
}

const emptyMovements = (): CashMovements => ({
    cashSales: 0,
    cardSales: 0,
    transferSales: 0,
    totalSales: 0,
    ordersCount: 0,
    otherInputs: 0,
    otherOutputs: 0,
    shipping: 0
});

// Los medios de pago se leen SIEMPRE por `getOrderPaidBy` (@api/Orders/OrderPayments),
// que entiende tanto el modelo nuevo (`order.payments[]`) como el viejo (los 3
// escalares). Es lo que permite que las órdenes de los dos modelos convivan sin
// que el corte cambie ni un peso — ver PLAN-MODELO-PAGOS-Y-ORIGEN-ORDENES §4.
const movementsOf = (flows: CashFlow[], orders: Order[]): CashMovements => ({
    cashSales: sum(orders, (order) => getOrderPaidBy(order, PaymentMethod.Cash)),
    cardSales: sum(orders, (order) => getOrderPaidBy(order, PaymentMethod.CreditCard)),
    transferSales: sum(orders, (order) => getOrderPaidBy(order, PaymentMethod.BankTransfer)),
    totalSales: sum(orders, (order) => order.total),
    ordersCount: orders.length,
    // Solo lo que movió efectivo y sigue activo (`@utils/CashFlows`): un gasto
    // cancelado o pagado fuera de la caja no toca el esperado.
    otherInputs: sum(flows.filter(isCashOtherInput), (flow) => flow.amount),
    otherOutputs: sum(flows.filter(isCashOutput), (flow) => flow.amount),
    shipping: sum(flows.filter(isCashShipping), (flow) => flow.amount)
});

const addMovements = (a: CashMovements, b: CashMovements): CashMovements => ({
    cashSales: a.cashSales + b.cashSales,
    cardSales: a.cardSales + b.cardSales,
    transferSales: a.transferSales + b.transferSales,
    totalSales: a.totalSales + b.totalSales,
    ordersCount: a.ordersCount + b.ordersCount,
    otherInputs: a.otherInputs + b.otherInputs,
    otherOutputs: a.otherOutputs + b.otherOutputs,
    shipping: a.shipping + b.shipping
});

/** fondo + ventas en efectivo + otros ingresos − otros gastos − envíos */
export const getExpectedCash = (initialCash: number, movements: CashMovements): number =>
    initialCash + movements.cashSales + movements.otherInputs - movements.otherOutputs - movements.shipping;

/**
 * Ventana de cada caja. Cerrada: de su apertura a su `closedAt`. Abierta: hasta
 * que abre la siguiente, o hasta ahora (sin pasarse del fin del día que se mira).
 * Las cajas viejas sin `closedAt` (anteriores al corte ciego) se estiran hasta la
 * apertura de la siguiente o el fin del día: es lo más fiel que se puede sin dato.
 */
export const getCashRegisterWindows = (
    registers: CashRegister[],
    dayEnd: Date,
    now: Date = new Date()
): { register: CashRegister; from: Date; to: Date }[] => {
    const sorted = [...registers].sort((a, b) => getCashRegisterMillis(a.date) - getCashRegisterMillis(b.date));
    const cap = Math.min(dayEnd.getTime(), now.getTime());
    return sorted.map((register, index) => {
        const from = getCashRegisterDate(register.date);
        const closedAt = register.closedAt ? getCashRegisterMillis(register.closedAt) : undefined;
        const nextOpening = sorted[index + 1] ? getCashRegisterMillis(sorted[index + 1].date) : undefined;
        const openUntil = Math.min(nextOpening ?? Number.POSITIVE_INFINITY, Math.max(cap, from.getTime()));
        return { register, from, to: new Date(closedAt ?? openUntil) };
    });
};

export const buildBranchCashSummaries = (
    branchOffices: BranchOffice[],
    cashRegisters: CashRegister[],
    cashFlows: CashFlow[],
    orders: Order[],
    options?: { dayEnd?: Date; now?: Date }
): BranchCashSummary[] => {
    const now = options?.now || new Date();
    const dayEnd = options?.dayEnd || now;

    return branchOffices
        .filter((branchOffice) => !!branchOffice.id)
        .map((branchOffice) => {
            const branchOfficeId = branchOffice.id as string;
            const branchFlows = cashFlows.filter((flow) => flow.branchOfficeId === branchOfficeId);
            const branchOrders = orders.filter((order) => order.branchOfficeId === branchOfficeId);
            const windows = getCashRegisterWindows(
                cashRegisters.filter((register) => register.branchOfficeId === branchOfficeId),
                dayEnd,
                now
            );

            const usedFlows = new Set<CashFlow>();
            const usedOrders = new Set<Order>();

            const registers: CashRegisterSummary[] = windows.map(({ register, from, to }) => {
                const inWindow = (date: any) => {
                    const time = getCashRegisterMillis(date);
                    return time >= from.getTime() && time < to.getTime();
                };
                const flows = branchFlows.filter((flow) => inWindow(flow.date));
                const windowOrders = branchOrders.filter((order) => inWindow(order.date));
                flows.forEach((flow) => usedFlows.add(flow));
                windowOrders.forEach((order) => usedOrders.add(order));

                const movements = movementsOf(flows, windowOrders);
                const initialCash = register.initialCash || 0;
                return {
                    ...movements,
                    register,
                    isOpen: register.status === CashRegisterStatus.Open,
                    from,
                    to,
                    initialCash,
                    expectedCash: getExpectedCash(initialCash, movements),
                    countedCash: register.countedCash,
                    difference: register.difference,
                    hasBlindCount: typeof register.countedCash === 'number'
                };
            });

            // Lo que no cayó en ninguna ventana: ventas sin caja abierta o entre turnos
            const outsideMovements = movementsOf(
                branchFlows.filter((flow) => !usedFlows.has(flow)),
                branchOrders.filter((order) => !usedOrders.has(order))
            );

            const totals = registers.reduce<CashMovements>(
                (acc, summary) => addMovements(acc, summary),
                emptyMovements()
            );
            const dayMovements = addMovements(totals, outsideMovements);
            const initialCash = sum(registers, (summary) => summary.initialCash);
            const withCount = registers.filter((summary) => summary.hasBlindCount);

            return {
                ...dayMovements,
                branchOfficeId,
                branchOfficeName: branchOffice.name,
                registers,
                openRegisters: registers.filter((summary) => summary.isOpen).length,
                hasRegister: registers.length > 0,
                initialCash,
                expectedCash: getExpectedCash(initialCash, dayMovements),
                outside: { ...outsideMovements, expectedCash: getExpectedCash(0, outsideMovements) },
                difference: sum(withCount, (summary) => summary.difference || 0),
                registersWithCount: withCount.length
            };
        })
        .sort((a, b) => a.branchOfficeName.localeCompare(b.branchOfficeName));
};

export interface CashRegistersDayTotals {
    branchesWithRegister: number;
    branchesWithoutRegister: number;
    openRegisters: number;
    expectedCash: number;
    totalSales: number;
    ordersCount: number;
    /** Suma de diferencias de los cortes ciegos del día */
    difference: number;
    registersWithCount: number;
    /** Sucursales con ventas que ocurrieron sin ninguna caja abierta */
    branchesWithSalesOutside: number;
}

export const getCashRegistersDayTotals = (summaries: BranchCashSummary[]): CashRegistersDayTotals => ({
    branchesWithRegister: summaries.filter((summary) => summary.hasRegister).length,
    branchesWithoutRegister: summaries.filter((summary) => !summary.hasRegister).length,
    openRegisters: sum(summaries, (summary) => summary.openRegisters),
    expectedCash: sum(summaries, (summary) => summary.expectedCash),
    totalSales: sum(summaries, (summary) => summary.totalSales),
    ordersCount: sum(summaries, (summary) => summary.ordersCount),
    difference: sum(summaries, (summary) => summary.difference),
    registersWithCount: sum(summaries, (summary) => summary.registersWithCount),
    branchesWithSalesOutside: summaries.filter((summary) => summary.outside.ordersCount > 0).length
});
